import type { TaskWithFlags } from "@desk/contracts";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useConcept } from "@/features/concept-provider";
import { useHierarchy, useTask } from "@/data/hooks";
import { useTaskActions } from "@/data/actions";
import { openTask, useUi } from "@/features/ui-state";
import { taskRegistry } from "@/features/list/registry";
import { useTaskDetailParts } from "./detail-parts";

export function DetailBody({ task, mode }: { task: TaskWithFlags; mode: "panel" | "sheet" }) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const a = useTaskActions();
  const parts = useTaskDetailParts(task);
  if (!parts) return null;
  return (
    <concept.TaskDetail
      task={task}
      mode={mode}
      today={h.today}
      timezone={h.timezone}
      onClose={() => openTask(null)}
      onToggleComplete={() => void a.toggleComplete(task)}
      parts={parts}
    />
  );
}

/** Centered sheet presentation (used for overlay opens and by sheet-mode concepts). */
export function DetailSheet({ task }: { task: TaskWithFlags }) {
  return (
    <Dialog open onOpenChange={(o) => !o && openTask(null)}>
      <DialogContent position="top" hideClose className="max-h-[78vh] max-w-[640px] overflow-y-auto p-0">
        <DialogTitle className="sr-only">
          {task.readableId} {task.title}
        </DialogTitle>
        <DetailBody task={task} mode="sheet" />
      </DialogContent>
    </Dialog>
  );
}

/** Resolves the open task (from lists, cache, or the API) and presents it per concept. */
export function useOpenTaskModel() {
  const openId = useUi((s) => s.openTaskId);
  const via = useUi((s) => s.detailVia);
  const fromList = openId ? taskRegistry.get(openId) : undefined;
  const { task: fetched } = useTask(openId);
  const task = fetched ?? fromList;
  return { openId, via, task };
}

/** Panel content for concepts with detailMode "panel" (rendered by Shell's detailPanel slot). */
export function useDetailPanel() {
  const { concept } = useConcept();
  const { via, task } = useOpenTaskModel();
  if (concept.detailMode !== "panel" || via !== "list" || !task) return undefined;
  return <DetailBody key={task.id} task={task} mode="panel" />;
}

/** Root-level sheet: sheet-mode concepts, and overlay opens (palette, deep links, boards). */
export function TaskDetailOverlay() {
  const { concept } = useConcept();
  const { via, task } = useOpenTaskModel();
  if (!task) return null;
  if (via === "overlay" || concept.detailMode === "sheet") return <DetailSheet key={task.id} task={task} />;
  return null;
}
